/**
 * Klassen-Definition und Farbzuordnung "schlecht / ok / gut".
 *
 * EINE einzige Quelle der Wahrheit — überall genutzt: Donut-Diagramme,
 * Legende, Polygon-Füllung, Info-Panel. Reihenfolge und Farben sind hier
 * gekapselt und trivial umsortierbar (offener Punkt §10 des Plans).
 *
 * Standard: gut = Grün (vital), ok = Gelb (auffällig), schlecht = Rot
 * (kritisch) — eine klassische Ampel. Auf grauer Basiskarte heben sich
 * alle drei klar ab.
 */

export type KlassenKey = 'schlecht' | 'ok' | 'gut';

export interface Klasse {
  key: KlassenKey;
  label: string;
  farbe: string;
}

export const KLASSEN: readonly Klasse[] = [
  { key: 'gut', label: 'vital', farbe: '#47DB00' }, // Grün
  { key: 'ok', label: 'auffällig', farbe: '#FDC300' }, // Gelb
  { key: 'schlecht', label: 'kritisch', farbe: '#E7003A' }, // Rot
] as const;

/** Verteilungsrohwerte (Prozent ODER absolute Zählungen — Frontend normiert selbst). */
export type Verteilung = Record<KlassenKey, number>;

/** Summe aller Klassenwerte (0, falls alle fehlen). */
export function verteilungSumme(v: Verteilung | undefined): number {
  if (!v) return 0;
  return KLASSEN.reduce((sum, k) => sum + (Number(v[k.key]) || 0), 0);
}

/**
 * Normiert eine Verteilung auf Anteile (Summe = 1) in KLASSEN-Reihenfolge.
 * Gibt null zurück, wenn keine verwertbaren Werte vorliegen (Donut wird
 * dann ausgelassen — kein Fehler, s. §5 des Plans).
 */
export function normiereVerteilung(
  v: Verteilung | undefined
): { key: KlassenKey; anteil: number; farbe: string; label: string }[] | null {
  const summe = verteilungSumme(v);
  if (summe <= 0) return null;
  return KLASSEN.map((k) => ({
    key: k.key,
    label: k.label,
    farbe: k.farbe,
    anteil: (Number(v![k.key]) || 0) / summe,
  }));
}

/** Dominante Klasse (höchster Anteil) — für Polygon-Füllung/Schnellüberblick. */
export function dominanteKlasse(v: Verteilung | undefined): Klasse | null {
  const norm = normiereVerteilung(v);
  if (!norm) return null;
  let best = norm[0];
  for (const n of norm) if (n.anteil > best.anteil) best = n;
  return KLASSEN.find((k) => k.key === best.key) ?? null;
}
